import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAPlantioOutraCultura } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';

interface PlantioOutraCulturaModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAPlantioOutraCultura) => void;
	dadosPlantioOutraCultura?: CadernoLPMAPlantioOutraCultura | null;
}

const defaultValues: CadernoLPMAPlantioOutraCultura = {
	dataInicioPlantioOutraCultura: null,
	dataTerminoPlantioOutraCultura: null,
	responsavelPlantioOutraCultura: '',
	previsaoLPMA: '',
	dataAutorizacao: null,
	plantioOutraCulturaJson: '',
	limpezaPlantioOutraCulturaJson: '',
	observacoesPlantioOutraCultura: '',
};

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

export default function PlantioOutraCulturaModal({ show, onHide, onSubmit, dadosPlantioOutraCultura }: PlantioOutraCulturaModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		setValue,
		getValues,
	} = useForm<CadernoLPMAPlantioOutraCultura>({
		mode: 'all',
		defaultValues,
	});

	const [linhasPlantio, setLinhasPlantio] = useState<number>(1);
	const [linhasLimpeza, setLinhasLimpeza] = useState<number>(1);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setLinhasPlantio(1);
			setLinhasLimpeza(1);
		} else if (dadosPlantioOutraCultura) {
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse dos JSONs
			let plantios: any[] = [];
			let limpezas: any[] = [];
			try {
				if (dadosPlantioOutraCultura.plantioOutraCulturaJson) {
					plantios = JSON.parse(dadosPlantioOutraCultura.plantioOutraCulturaJson);
				}
			} catch (e) {}
			try {
				if (dadosPlantioOutraCultura.limpezaPlantioOutraCulturaJson) {
					limpezas = JSON.parse(dadosPlantioOutraCultura.limpezaPlantioOutraCulturaJson);
				}
			} catch (e) {}

			setLinhasPlantio(Math.max(1, plantios.length));
			setLinhasLimpeza(Math.max(1, limpezas.length));

			const dadosParaReset: any = {
				...dadosPlantioOutraCultura,
				dataInicioPlantioOutraCultura: parseDate(dadosPlantioOutraCultura.dataInicioPlantioOutraCultura),
				dataTerminoPlantioOutraCultura: parseDate(dadosPlantioOutraCultura.dataTerminoPlantioOutraCultura),
				dataAutorizacao: parseDate(dadosPlantioOutraCultura.dataAutorizacao),
			};

			plantios.forEach((item: any, index: number) => {
				dadosParaReset[`talhao${index}`] = item.talhao || '';
				dadosParaReset[`cultura${index}`] = item.cultura || '';
				dadosParaReset[`area${index}`] = item.area || '';
			});

			limpezas.forEach((item: any, index: number) => {
				dadosParaReset[`limpezaNome${index}`] = item.nome || '';
				dadosParaReset[`limpezaDataAntes${index}`] = parseDate(item.dataAntes);
				dadosParaReset[`limpezaDataDepois${index}`] = parseDate(item.dataDepois);
				dadosParaReset[`limpezaMetodo${index}`] = item.metodo || '';
				dadosParaReset[`limpezaResponsavel${index}`] = item.responsavel || '';
			});

			reset(dadosParaReset);
		}
	}, [show, dadosPlantioOutraCultura, reset]);

	const adicionarLinhaPlantio = () => setLinhasPlantio((prev) => prev + 1);
	const removerLinhaPlantio = (index: number) => {
		if (linhasPlantio === 1) return;
		setLinhasPlantio((prev) => prev - 1);
		setValue(`plantioData${index}` as any, null);
		setValue(`plantioTalhao${index}` as any, '');
		setValue(`plantioCultura${index}` as any, '');
	};

	const adicionarLinhaLimpeza = () => setLinhasLimpeza((prev) => prev + 1);
	const removerLinhaLimpeza = (index: number) => {
		if (linhasLimpeza === 1) return;
		setLinhasLimpeza((prev) => prev - 1);
		setValue(`limpezaNome${index}` as any, '');
		setValue(`limpezaDataEntrada${index}` as any, null);
		setValue(`limpezaDataSaida${index}` as any, null);
		setValue(`limpezaMetodo${index}` as any, '');
		setValue(`limpezaResponsavel${index}` as any, '');
	};

	const handleSubmitInterno = (dados: CadernoLPMAPlantioOutraCultura) => {
		const valores = getValues() as Record<string, any>;
		const dadosNormalizados: Record<string, any> = { ...dados };

		const registrosPlantio: any[] = [];
		for (let i = 0; i < linhasPlantio; i++) {
			const dataPlantio = valores[`plantioData${i}`];
			const talhao = valores[`plantioTalhao${i}`];
			const cultura = valores[`plantioCultura${i}`];

			if (dataPlantio || talhao || cultura) {
				registrosPlantio.push({
					data: formatarDataParaJson(dataPlantio),
					talhao,
					cultura,
				});
			}

			delete dadosNormalizados[`plantioData${i}`];
			delete dadosNormalizados[`plantioTalhao${i}`];
			delete dadosNormalizados[`plantioCultura${i}`];
		}

		const registrosLimpeza: any[] = [];
		for (let i = 0; i < linhasLimpeza; i++) {
			const nome = valores[`limpezaNome${i}`];
			const dataEntrada = valores[`limpezaDataEntrada${i}`];
			const dataSaida = valores[`limpezaDataSaida${i}`];
			const metodo = valores[`limpezaMetodo${i}`];
			const responsavel = valores[`limpezaResponsavel${i}`];

			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				registrosLimpeza.push({
					nome,
					dataAntes: formatarDataParaJson(dataEntrada),
					dataDepois: formatarDataParaJson(dataSaida),
					metodo,
					responsavel,
				});
			}

			delete dadosNormalizados[`limpezaNome${i}`];
			delete dadosNormalizados[`limpezaDataEntrada${i}`];
			delete dadosNormalizados[`limpezaDataSaida${i}`];
			delete dadosNormalizados[`limpezaMetodo${i}`];
			delete dadosNormalizados[`limpezaResponsavel${i}`];
		}

		dadosNormalizados.plantioOutraCulturaJson = registrosPlantio.length ? JSON.stringify(registrosPlantio) : null;
		dadosNormalizados.limpezaPlantioOutraCulturaJson = registrosLimpeza.length ? JSON.stringify(registrosLimpeza) : null;

		onSubmit(dadosNormalizados as CadernoLPMAPlantioOutraCultura);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.PLANTIOOUTRACULTURA_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de acesso dos envolvidos à área da LPMA e datas inicial/final de realização das atividades</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							<Controller
								control={control}
								name="dataInicioPlantioOutraCultura"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							<Controller
								control={control}
								name="dataTerminoPlantioOutraCultura"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Col>
				</Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Nome e assinatura</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							ref={register}
							name="responsavelPlantioOutraCultura"
							placeholder="Liste os envolvidos e, se necessário, observações de assinatura"
							maxLength={300}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de plantio de outra cultura na área da LPMA</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>É previsto na LPMA / Parecer Técnico o plantio de outra cultura na área da LPMA?</Form.Label>
						<div className="d-flex">
							<Form.Check
								type="radio"
								ref={register}
								name="previsaoLPMA"
								value="true"
								label="Sim"
								className="mr-3"
							/>
							<Form.Check
								type="radio"
								ref={register}
								name="previsaoLPMA"
								value="false"
								label="Não"
							/>
						</div>
						<p className="mt-2 mb-2">
							<small>
								* Somente culturas sexualmente incompatíveis com a cultura objeto da LPMA podem ser plantadas na área.
								As sementes oriundas deste plantio não poderão ser utilizadas para multiplicação futura.
							</small>
						</p>
						<p className="mb-2">
							<small>
								Em caso negativo, a CIBio autorizou o referido plantio em:
							</small>
						</p>
						<Controller
							control={control}
							name="dataAutorizacao"
							render={({ onChange, value }) => (
								<DatePicker
									selected={value || null}
									onChange={onChange}
									className="input-custom-to-date"
									locale="pt-BR"
									dateFormat="dd/MM/yyyy"
									placeholderText="Selecionar a data"
								/>
							)}
						/>
						<p className="mt-2">
							<small>
								* Anexar a comunicação / autorização do plantio de outra cultura na pasta de campo – ABA COMUNICAÇÃO INTERNA.
							</small>
						</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Registros de plantio</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={4}>Data do plantio</Col>
					<Col md={4}>Talhão</Col>
					<Col md={4}>Cultura plantada</Col>
				</Row>

				{Array.from({ length: linhasPlantio }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`plantio-${index}`}>
						<Col md={4}>
							<Controller
								control={control}
								name={`plantioData${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={4}>
							<Form.Control ref={register} name={`plantioTalhao${index}`} placeholder="Talhão" maxLength={150} />
						</Col>
						<Col md={4}>
							<Form.Control ref={register} name={`plantioCultura${index}`} placeholder="Cultura plantada" maxLength={150} />
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{linhasPlantio > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaPlantio(index)} />
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar registro" contained={false} type="button" onClick={adicionarLinhaPlantio} />
				</div>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de limpeza de maquinários / equipamentos utilizados no plantio</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={4}>Nome e código do maquinário / equipamento</Col>
					<Col md={2}>Data de limpeza – Antes (entrada)</Col>
					<Col md={2}>Data de limpeza – Antes (saída)</Col>
					<Col md={2}>Método de limpeza</Col>
					<Col md={2}>Responsável</Col>
				</Row>

				{Array.from({ length: linhasLimpeza }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`limpeza-plantio-${index}`}>
						<Col md={4}>
							<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Maquinário / equipamento" maxLength={150} />
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`limpezaDataEntrada${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`limpezaDataSaida${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`limpezaMetodo${index}`} placeholder="Método" maxLength={150} />
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" maxLength={150} />
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{linhasLimpeza > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaLimpeza(index)} />
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar maquinário" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
				</div>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesPlantioOutraCultura"
							placeholder="Registre observações adicionais sobre o plantio da outra cultura"
							maxLength={500}
						/>
					</Form.Group>
				</Form.Row>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Inserir" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

